import { useId, useState, type ReactElement } from 'react';
import { ThumbsUp, Users, X } from 'lucide-react';
import { ApiError, type PublicProfile } from '@molt/shared';

import { Avatar, Button, IconButton, Popover, Skeleton, profileName } from '@/components';
import { announce } from '@/lib/announce';
import {
  useInterfaceLanguage,
  type InterfaceLanguageContextValue,
} from '@/lib/interface-language';
import {
  useCardVoteState,
  useCardVoteTally,
  useUnvoteForCard,
  useVoteForCard,
} from '@/lib/votes';

/**
 * FE-80.1. The two module-scope helpers below take a translator as a REQUIRED
 * first parameter rather than reading the context, on `lib/card-dates.ts`'s
 * rule: a non-component function cannot call a hook, and a *required* leading
 * parameter makes a missed call site a `tsc` error instead of an English
 * literal that renders forever.
 *
 * ⚠ `AccessibilityMenuSection` and `AppearanceMenuSection` declare this same
 * alias under this same name but make it OPTIONAL. Optionality is exactly what
 * stops `tsc` enumerating the call sites, so the alias is taken from them and
 * the optionality is not.
 */
type InterfaceTranslator = InterfaceLanguageContextValue['t'];

export interface CardVoteSectionProps {
  readonly cardId: string;
  readonly boardId: string;
  readonly boardClosed: boolean;
  readonly readOnlyReason?: string | undefined;
  /**
   * Render nothing while the board's vote state is loading or voting is off.
   * The card back passes it: `FR-CARD-033` asks for a Vote control only "when
   * voting is enabled for the board", and a block saying voting is off spends
   * the meta row on nothing. The standalone default keeps the stated reason.
   */
  readonly hideWhenOff?: boolean | undefined;
}

export function CardVoteSection({
  cardId,
  boardId,
  boardClosed,
  readOnlyReason,
  hideWhenOff = false,
}: CardVoteSectionProps): ReactElement | null {
  const { t } = useInterfaceLanguage();
  const headingId = useId();
  const [tallyOpen, setTallyOpen] = useState(false);
  const voteState = useCardVoteState(cardId);
  const vote = useVoteForCard();
  const unvote = useUnvoteForCard();

  const state = voteState.data;
  const countVisible =
    state !== undefined &&
    state.votingEnabled &&
    !state.talliesHidden &&
    state.voteCount !== null;
  const tally = useCardVoteTally(cardId, tallyOpen && countVisible);
  // ⚠ `readOnlyReason` is authored by `CardBack`, not here — it arrives already
  // routed from `t('cardBack.reason.archivedReadOnly')`. Only the fallback
  // branch is this file's copy.
  const locked =
    state === undefined
      ? undefined
      : (readOnlyReason ?? lockedReason(t, state, boardClosed));
  const mutationPending = vote.isPending || unvote.isPending;
  const mutationError = vote.error ?? unvote.error;
  const voteCount = state?.voteCount ?? 0;

  function toggleVote() {
    if (state === undefined || locked !== undefined) return;
    const mutation = state.voted ? unvote : vote;
    mutation.mutate(
      { boardId, cardId },
      {
        onSuccess: (nextState) => {
          announce(
            nextState.voted
              ? t('cardBack.votes.announce.added')
              : t('cardBack.votes.announce.removed'),
          );
        },
      },
    );
  }

  if (
    hideWhenOff &&
    (voteState.isPending || (state !== undefined && !state.votingEnabled))
  ) {
    return null;
  }

  return (
    <section
      aria-labelledby={headingId}
      className="flex flex-col gap-2 rounded-card border border-border bg-surface-raised px-3 py-2.5"
    >
      <div className="flex items-center gap-2">
        <ThumbsUp aria-hidden="true" className="size-4 text-ink-muted" />
        <h2 id={headingId} className="text-sm font-semibold text-ink">
          {t('cardBack.votes.heading')}
        </h2>
      </div>

      {voteState.isPending ? (
        <div
          aria-label={t('cardBack.votes.loadingState')}
          className="flex flex-col gap-2"
        >
          <Skeleton shape="line" width={112} />
          <Skeleton shape="line" width={176} />
        </div>
      ) : voteState.isError ? (
        <p role="status" className="text-sm text-bad">
          {t('cardBack.votes.loadFailed')}
        </p>
      ) : !state?.votingEnabled ? (
        <p className="text-sm text-ink-muted">{t('cardBack.votes.disabled')}</p>
      ) : (
        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap items-center gap-2">
            {state.voted || state.mayVote ? (
              <Button
                size="sm"
                variant="toggle"
                pressed={state.voted}
                disabled={locked !== undefined}
                disabledReason={locked}
                busy={mutationPending}
                busyLabel={
                  state.voted ? t('cardBack.votes.removing') : t('cardBack.votes.adding')
                }
                aria-label={
                  state.voted
                    ? t('cardBack.votes.votedLabel')
                    : t('cardBack.votes.voteLabel')
                }
                iconStart={<ThumbsUp aria-hidden="true" className="size-4" />}
                onClick={toggleVote}
              >
                {state.voted ? t('cardBack.votes.voted') : t('cardBack.votes.vote')}
              </Button>
            ) : null}

            {countVisible ? (
              <Popover
                open={tallyOpen}
                onOpenChange={setTallyOpen}
                title={t('cardBack.votes.popoverTitle')}
                width={280}
                trigger={
                  <Button
                    size="sm"
                    variant="quiet"
                    aria-label={t(
                      'cardBack.votes.countLabel',
                      { count: voteCount },
                      voteCount,
                    )}
                    iconStart={<Users aria-hidden="true" className="size-4" />}
                  >
                    {t('cardBack.votes.count', { count: voteCount }, voteCount)}
                  </Button>
                }
                closeControl={
                  <IconButton
                    label={t('cardBack.votes.closePopover')}
                    size="sm"
                    variant="quiet"
                  >
                    <X aria-hidden="true" className="size-4" />
                  </IconButton>
                }
              >
                <VoteTallyBody
                  pending={tally.isPending}
                  error={tally.error}
                  voters={tally.data?.voters}
                />
              </Popover>
            ) : null}
          </div>

          {locked === undefined ? null : (
            <p className="text-sm text-ink-muted">{locked}</p>
          )}

          {countVisible || !state.talliesHidden ? null : (
            <p className="text-sm text-ink-muted">{t('cardBack.votes.talliesHidden')}</p>
          )}

          {mutationError === null ? null : (
            <p role="status" className="text-sm text-bad">
              {voteMutationErrorMessage(t, mutationError)}
            </p>
          )}
        </div>
      )}
    </section>
  );
}

function VoteTallyBody({
  pending,
  error,
  voters,
}: {
  readonly pending: boolean;
  readonly error: Error | null;
  readonly voters: readonly PublicProfile[] | undefined;
}) {
  const { t } = useInterfaceLanguage();

  if (pending) {
    return (
      <div aria-label={t('cardBack.votes.loadingVoters')} className="flex flex-col gap-2">
        <Skeleton shape="line" />
        <Skeleton shape="line" width="75%" />
      </div>
    );
  }

  if (error !== null) {
    return (
      <p role="status" className="text-sm text-bad">
        {t('cardBack.votes.votersFailed')}
      </p>
    );
  }

  if (voters === undefined || voters.length === 0) {
    return <p className="text-sm text-ink-muted">{t('cardBack.votes.noVoters')}</p>;
  }

  return (
    <ul aria-label={t('cardBack.votes.votersList')} className="flex flex-col gap-2">
      {voters.map((voter) => (
        <li key={voter.id} className="flex min-w-0 items-center gap-2 text-sm text-ink">
          <Avatar profile={voter} size="sm" />
          <span className="min-w-0 truncate">{profileName(voter)}</span>
        </li>
      ))}
    </ul>
  );
}

function lockedReason(
  t: InterfaceTranslator,
  state: { readonly mayVote: boolean; readonly voted: boolean },
  boardClosed: boolean,
): string | undefined {
  if (boardClosed) return t('cardBack.votes.lockedBoardClosed');
  if (!state.mayVote) {
    return state.voted
      ? t('cardBack.votes.lockedVoted')
      : t('cardBack.votes.lockedByBoardSetting');
  }
  return undefined;
}

function voteMutationErrorMessage(t: InterfaceTranslator, error: Error): string {
  // ⚠ `'conflict'` is an `ApiError.code` wire value compared for equality, not
  // copy. Routing it would silently disable this branch.
  if (error instanceof ApiError && error.code === 'conflict') {
    return t('cardBack.votes.conflictBoardClosed');
  }
  return t('cardBack.votes.changeFailed');
}
